import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { AdminService, CollectionConfig, CollectionField } from './admin.service';
import { RowDialogComponent, RowDialogResult } from './row-dialog.component';

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss'],
    standalone: false
})
export class AdminComponent implements OnInit {
    mode: 'loading' | 'login' | 'ready' = 'loading';
    loginError = '';
    loggingIn = false;

    collections: CollectionConfig[] = [];
    activeIndex = 0;
    rows: Record<string, unknown>[] = [];
    dataSource = new MatTableDataSource<Record<string, unknown>>([]);
    loadingRows = false;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    email = '';
    password = '';

    constructor(
        private admin: AdminService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {}

    get activeCollection(): CollectionConfig | null {
        return this.collections[this.activeIndex] ?? null;
    }

    get columnNames(): string[] {
        return this.activeCollection
            ? this.activeCollection.fields.filter(field => !field.hideInTable).map(field => field.name)
            : [];
    }

    ngOnInit(): void {
        if (!this.admin.hasSession()) {
            this.mode = 'login';
            return;
        }
        this.loadCollections();
    }

    login(): void {
        if (!this.email || !this.password) return;
        this.loggingIn = true;
        this.loginError = '';
        this.admin.login(this.email, this.password).subscribe({
            next: () => this.loadCollections(),
            error: error => {
                this.loggingIn = false;
                this.loginError = error.message;
            }
        });
    }

    private loadCollections(): void {
        this.mode = 'loading';
        this.admin.getCollections().subscribe({
            next: collections => {
                this.collections = collections;
                this.activeIndex = 0;
                this.mode = 'ready';
                this.loadRows();
            },
            error: error => {
                this.loggingIn = false;
                this.admin.logout();
                this.mode = 'login';
                this.loginError = error.message === 'Sesión caducada. Inicia sesión de nuevo.'
                    ? 'La sesión caducó. Inicia sesión de nuevo.'
                    : 'No se pudo conectar con el servidor.';
            }
        });
    }

    switchCollection(index: number): void {
        this.activeIndex = index;
        this.loadRows();
    }

    loadRows(): void {
        const collection = this.activeCollection;
        if (!collection) return;
        this.loadingRows = true;
        this.admin.list<any>(collection.name).subscribe({
            next: rows => {
                this.rows = rows;
                this.dataSource.data = rows;
                this.loadingRows = false;
                this.attachPaginator();
            },
            error: error => {
                this.loadingRows = false;
                this.snackBar.open(error.message, 'Cerrar', { duration: 5000 });
                if (error.message === 'Sesión caducada. Inicia sesión de nuevo.') {
                    this.admin.logout();
                    this.mode = 'login';
                }
            }
        });
    }

    private attachPaginator(): void {
        setTimeout(() => {
            if (this.paginator) this.dataSource.paginator = this.paginator;
            if (this.sort) this.dataSource.sort = this.sort;
        });
    }

    applyFilter(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dataSource.filter = value.trim().toLowerCase();
    }

    cellText(row: Record<string, unknown>, field: CollectionField): string {
        const value = row[field.name];
        if (value == null || value === '') return '';
        if (field.type === 'select') {
            return field.options?.find(option => option.value === value)?.label ?? String(value);
        }
        return String(value);
    }

    preview(fileId: string | null) {
        return this.admin.preview(fileId);
    }

    fileId(raw: unknown): string | null {
        return typeof raw === 'string' ? raw : null;
    }

    newRow(): void {
        const collection = this.activeCollection;
        if (!collection) return;
        const dialogRef = this.dialog.open(RowDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            data: { config: collection, row: null }
        });
        dialogRef.afterClosed().subscribe(result => this.handleResult(result, null));
    }

    editRow(row: Record<string, unknown>): void {
        const collection = this.activeCollection;
        if (!collection) return;
        const workingRow = { ...row };
        const dialogRef = this.dialog.open(RowDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            data: { config: collection, row: workingRow }
        });
        dialogRef.afterClosed().subscribe(result => this.handleResult(result, workingRow));
    }

    private handleResult(result: RowDialogResult | undefined, row: Record<string, unknown> | null): void {
        const collection = this.activeCollection;
        if (!collection || !result) return;
        if (result.delete && row) {
            this.admin.delete(collection.name, Number(row[collection.pk])).subscribe({
                next: () => {
                    this.snackBar.open('Registro eliminado.', '', { duration: 2500 });
                    this.loadRows();
                },
                error: error => this.snackBar.open(error.message, 'Cerrar', { duration: 5000 })
            });
            return;
        }
        if (result.save) {
            const onSaved = () => {
                this.snackBar.open('Guardado.', '', { duration: 2500 });
                this.loadRows();
            };
            const onError = (error: Error) => this.snackBar.open(error.message, 'Cerrar', { duration: 5000 });
            if (row) {
                this.admin.update(collection.name, Number(row[collection.pk]), result.save).subscribe({
                    next: onSaved,
                    error: onError
                });
            } else {
                this.admin.create(collection.name, result.save).subscribe({
                    next: onSaved,
                    error: onError
                });
            }
        }
    }

    logout(): void {
        this.admin.logout();
        this.mode = 'login';
    }
}